'use client';

/**
 * Root Provider Tree - Next.js App Router
 *
 * Mirrors the provider nesting order from src/App.tsx exactly:
 *   QueryClientProvider → AnalyticsProvider → TooltipProvider → CountryProvider → AuthProvider
 *
 * react-router-dom fully removed. AuthProvider and AnalyticsProvider adapted to next/navigation.
 * Vite remnants cleaned - Next.js App Router is the primary runtime.
 */

import { Suspense, lazy, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { CountryProvider } from '@/contexts/CountryContext';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthLinkNotice } from '@/components/auth/AuthLinkNotice';

// ── Lazy providers - deferred exactly as in App.tsx ─────────────────────────

const AnalyticsProvider = lazy(() =>
  import('@/components/analytics/AnalyticsProvider').then(m => ({ default: m.AnalyticsProvider }))
);

// Defer Toaster/Sonner - not needed until a toast fires
const Toaster = lazy(() =>
  import('@/components/ui/toaster').then(m => ({ default: m.Toaster }))
);
const Sonner = lazy(() =>
  import('@/components/ui/sonner').then(m => ({ default: m.Toaster }))
);

// ── i18n eager loader - initialise before any component uses useTranslation ──
import '@/i18n';

// ── Singleton QueryClient - server always gets a fresh instance ──────────────
// Pattern recommended by TanStack Query docs for Next.js App Router.
function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,   // 5 min - matches PRICING tier in queryClient.ts
        gcTime: 30 * 60 * 1000,     // 30 min
        refetchOnWindowFocus: false,
        refetchOnReconnect: 'always',
        refetchOnMount: false,
        retry: 1,
        retryDelay: (i) => Math.min(1000 * 2 ** i, 10000),
        networkMode: 'offlineFirst',
        structuralSharing: true,
      },
      mutations: {
        retry: 1,
        retryDelay: 1000,
        networkMode: 'offlineFirst',
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    // Server: new client per request - prevents cross-request data leakage
    return makeQueryClient();
  }
  // Browser: reuse singleton across renders
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}

// ── Root Providers component ─────────────────────────────────────────────────

interface ProvidersProps {
  children: React.ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  // Stable reference: useState ensures the client isn't recreated on re-renders
  const queryClientRef = useRef<QueryClient | null>(null);
  if (!queryClientRef.current) {
    queryClientRef.current = getQueryClient();
  }

  return (
    <QueryClientProvider client={queryClientRef.current}>
      <AuthProvider>
        <CountryProvider>
          <TooltipProvider>

            {/* i18n initialized via import in providers.tsx */}

            {/*
              The lazy analytics provider only runs effects (analytics, RUM, page
              tracking) and renders nothing of its own, so it sits beside the page
              rather than around it. Wrapped around {children}, its lazy import put
              every page's content inside a suspended boundary: the server sent the
              page in a hidden div revealed by a script near the end of the HTML,
              and the main text painted about 4 seconds late on a throttled phone
              (Lighthouse LCP 4.8 s on /drugs/metformin, 16 October 2026).
            */}
            <Suspense fallback={null}>
              <AnalyticsProvider>{null}</AnalyticsProvider>
            </Suspense>

            {/* Deferred global toasts */}
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
            </Suspense>

            {/* An email link that confirmed the address but signed nobody in
                (opened on another device), or that had expired (2026-10-25). */}
            <AuthLinkNotice />

            {children}

          </TooltipProvider>
        </CountryProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
